import { useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { ImageIntentContext } from '../../../../utils/imageIntentReporter';
import { IMAGE_INTENT_ATTRIBUTE } from './ImageIntentTouchStyle';
import {
  resolveGallery,
  selectGalleryDefaults,
} from './imageIntentContextGallery';
import { selectImageProtectionEnabled } from '../../../../selectors/imageIntent';
import {
  createImageIntentGestureTracker,
  ImageIntentGestureTracker,
  pointerTypeOf,
  touchPointOf,
} from './imageIntentGestures';
import useImageIntentSaveShortcut from './useImageIntentSaveShortcut';

// Marks ImageProtectionOverlay's save shield. The shield reports the
// gestures that land on it itself, and its touch events must bubble (a
// carousel above it swipes on them), so friction handlers on an element that
// contains a shield skip events that came from it: one gesture, one report.
export const IMAGE_INTENT_SHIELD_ATTRIBUTE = 'data-image-intent-shield';

export interface FrictionProps {
  [IMAGE_INTENT_ATTRIBUTE]: string;
  onContextMenu: (event: React.MouseEvent) => void;
  onDragStart: (event: React.DragEvent) => void;
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchMove: (event: React.TouchEvent) => void;
  onTouchEnd: (event?: React.TouchEvent) => void;
  onTouchCancel: (event?: React.TouchEvent) => void;
}

// True for an event that bubbled up from a save shield nested inside the
// element whose handler is running; the shield's own handlers (target and
// current target are the shield) are not skipped.
const fromNestedShield = (event?: React.SyntheticEvent): boolean => {
  if (!event) return false;
  const { target, currentTarget } = event;
  return (
    target !== currentTarget &&
    target instanceof Element &&
    target.closest(`[${IMAGE_INTENT_SHIELD_ATTRIBUTE}]`) !== null
  );
};

interface UseImageIntentFriction {
  protectionEnabled: boolean;
  getFrictionProps: (context?: ImageIntentContext) => FrictionProps;
}

// Save-intent handlers for preview images. Spread the returned props onto the
// image (or its container — touch/context events bubble). Every attempt is
// reported, with whether it was blocked, and the gestures that save are
// blocked: right-click everywhere, drags (finger, mouse or trackpad), and —
// through the data attribute and ImageIntentTouchStyle — the iOS long-press
// save sheet (the last two only where the studio's save blocking is on, which
// image protection implies: isSaveBlockingEnabled). See
// createImageIntentGestureTracker. The studio's image protection flag
// (ENG-2594) is returned as `protectionEnabled` for the overlay call sites.
//
// Reports carry the Redux gallery unless the context names its own (see
// resolveGallery). Pass `saveShortcutContext` to also report Cmd/Ctrl+S while
// the surface is mounted (useImageIntentSaveShortcut; a screen that only needs
// that registration calls it directly).
const useImageIntentFriction = (
  saveShortcutContext?: ImageIntentContext,
): UseImageIntentFriction => {
  const trackerRef = useRef<ImageIntentGestureTracker>();
  if (!trackerRef.current) {
    trackerRef.current = createImageIntentGestureTracker();
  }
  const tracker = trackerRef.current;

  const galleryDefaults = useSelector(selectGalleryDefaults, shallowEqual);
  const protectionEnabled = useSelector(selectImageProtectionEnabled);

  // A touch that starts just before the component unmounts (navigation, modal
  // close) must not fire a stale long-press report after the surface is gone.
  useEffect(() => () => tracker.dispose(), [tracker]);

  useImageIntentSaveShortcut(saveShortcutContext);

  const getFrictionProps = (
    context: ImageIntentContext = {},
  ): FrictionProps => {
    const reported: ImageIntentContext = {
      ...context,
      ...resolveGallery(context, galleryDefaults),
    };

    return {
      [IMAGE_INTENT_ATTRIBUTE]: '',
      // React 16's synthetic MouseEvent has no pointerType; the native event
      // (a PointerEvent in Chromium) says whether a finger opened the menu.
      onContextMenu: (event: React.MouseEvent) => {
        if (fromNestedShield(event)) return;
        tracker.contextMenu(event, reported, pointerTypeOf(event.nativeEvent));
      },
      onDragStart: (event: React.DragEvent) => {
        if (fromNestedShield(event)) return;
        tracker.dragStart(event, reported);
      },
      onTouchStart: (event: React.TouchEvent) => {
        if (fromNestedShield(event)) return;
        tracker.touchStart(reported, touchPointOf(event), event.touches.length);
      },
      onTouchMove: (event: React.TouchEvent) => {
        if (fromNestedShield(event)) return;
        tracker.touchMove(touchPointOf(event), event.touches.length);
      },
      onTouchEnd: (event?: React.TouchEvent) => {
        if (fromNestedShield(event)) return;
        tracker.touchEnd();
      },
      onTouchCancel: (event?: React.TouchEvent) => {
        if (fromNestedShield(event)) return;
        tracker.touchCancel(reported);
      },
    };
  };

  return { protectionEnabled, getFrictionProps };
};

export default useImageIntentFriction;
